import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { SkeletonCard } from '@/components/ui/SkeletonLoader';
import { ConfirmDialog, useConfirmDialog } from '@/components/ui/ConfirmDialog';
import { estagioHorarioService, estagioService } from '@/services/api';
import { PaginationParams } from '@/types';
import { PlusIcon, ClockIcon, EyeIcon, CalendarIcon, UserIcon, BuildingOfficeIcon, TrashIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

export const HorariosPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { dialogState, showConfirm, closeDialog } = useConfirmDialog();
  const [filters, setFilters] = useState<PaginationParams>({
    page: 1,
    limit: 10,
  });
  const [searchText, setSearchText] = useState<string>("");
  const [deletingEstagioId, setDeletingEstagioId] = useState<number | null>(null);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setFilters(prev => ({ ...prev, page: 1, search: searchText || undefined }));
    }, 400);
    return () => clearTimeout(timeout);
  }, [searchText]);

  // Buscar estágios (estagiários) para mostrar informações relacionadas
  const { data: estagiosData, isLoading, error } = useQuery({
    queryKey: ['estagios', filters],
    queryFn: () => estagioService.list(filters),
  });

  // Buscar todos os horários para agrupar por estágio
  const { data: horariosData } = useQuery({
    queryKey: ['horarios'],
    queryFn: () => estagioHorarioService.list({ page: 1, limit: 10000 }),
  });

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const getHorariosPorEstagio = (estagioId: number) => {
    return horariosData?.data?.filter(h => h.estagioId === estagioId) || [];
  };

  const getDiaSemanaLabel = (dia: string) => {
    const dias = {
      'Segunda': 'Segunda-feira',
      'Terca': 'Terça-feira',
      'Quarta': 'Quarta-feira',
      'Quinta': 'Quinta-feira',
      'Sexta': 'Sexta-feira',
      'Sabado': 'Sábado',
      'Domingo': 'Domingo'
    };
    return dias[dia as keyof typeof dias] || dia;
  };

  const formatarHorario = (horario: string) => {
    // Se for um DateTime ISO, extrair apenas a parte do horário usando UTC
    if (horario.includes('T')) {
      const date = new Date(horario);
      return `${date.getUTCHours().toString().padStart(2, '0')}:${date.getUTCMinutes().toString().padStart(2, '0')}`;
    }
    return horario;
  };

  const handleDeletarTodosHorarios = async (estagioId: number, alunoNome: string) => {
    showConfirm(
      'Excluir Todos os Horários',
      `Tem certeza que deseja deletar TODOS os horários do estagiário "${alunoNome}"?\n\nEsta ação não pode ser desfeita!`,
      async () => {
        setDeletingEstagioId(estagioId);

        try {
          // Usar o novo endpoint otimizado do backend
          const resultado = await estagioHorarioService.deleteByEstagio(estagioId);

          // Invalidar o cache para atualizar a lista
          await queryClient.invalidateQueries({ queryKey: ['horarios'] });
          await queryClient.invalidateQueries({ queryKey: ['estagios'] });

          toast.success(resultado.mensagem || 'Horários excluídos com sucesso!');
        } catch (error: unknown) {
          console.error('Erro ao deletar horários:', error);
          
          // Tratar mensagem de erro
          const mensagemErro = (error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Erro ao deletar horários. Por favor, tente novamente.';
          toast.error(mensagemErro);
        } finally {
          setDeletingEstagioId(null);
        }
      },
      'danger'
    );
  };

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">Erro ao carregar estágios</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Estagiários e Horários</h1>
          <p className="mt-1 text-sm text-gray-500">
            Gerencie os horários dos estagiários cadastrados
          </p>
        </div>
          <Link to="/horarios/novo">
        <Button asChild>
            <PlusIcon className="h-4 w-4 mr-2" />
            Novo Horário
        </Button>
          </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <UserIcon className="h-8 w-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Estagiários</p>
                <p className="text-2xl font-bold text-gray-900">{estagiosData?.pagination?.total || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <BuildingOfficeIcon className="h-8 w-8 text-purple-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Esta Página</p>
                <p className="text-2xl font-bold text-gray-900">{estagiosData?.data?.length || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <CalendarIcon className="h-8 w-8 text-orange-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Páginas</p>
                <p className="text-2xl font-bold text-gray-900">{estagiosData?.pagination?.totalPages || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Lista de Estagiários */}
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <CardTitle>Lista de Estagiários</CardTitle>
              <CardDescription>
                Gerencie os horários dos estagiários cadastrados no sistema
              </CardDescription>
            </div>
            <div className="w-full md:w-72 relative">
              <input
                type="text"
                placeholder="Buscar por aluno, empresa ou instituição"
                className="w-full border pl-3 pr-8 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
              {searchText && (
                <button
                  type="button"
                  aria-label="Limpar busca"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  onClick={() => setSearchText("")}
                >
                  ×
                </button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <SkeletonCard count={5} />
          ) : estagiosData?.data?.length === 0 ? (
            <div className="text-center py-8">
              <UserIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum estagiário encontrado</h3>
              <p className="text-gray-500 mb-4">Comece criando um novo estágio.</p>
                <Link to="/estagios/novo">
              <Button asChild>
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Novo Estágio
              </Button>
                </Link>
            </div>
          ) : (
            <div className="space-y-6">
              {estagiosData?.data?.map((estagio) => {
                const horarios = getHorariosPorEstagio(estagio.id);
                return (
                  <div
                    key={estagio.id}
                    className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow bg-white"
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-3">
                          <UserIcon className="h-6 w-6 text-blue-600" />
                          <div>
                            <h3 className="text-xl font-semibold text-gray-900">
                              {estagio.aluno?.nome || 'Nome não encontrado'}
                            </h3>
                            <p className="text-sm text-gray-500">Estágio #{estagio.id}</p>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          <div className="flex items-center space-x-2">
                            <BuildingOfficeIcon className="h-4 w-4 text-gray-400" />
                            <div>
                              <p className="text-gray-500">Empresa</p>
                              <p className="font-medium text-gray-900">
                                {estagio.empresa?.nome || 'Empresa não encontrada'}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <CalendarIcon className="h-4 w-4 text-gray-400" />
                            <div>
                              <p className="text-gray-500">Período</p>
                              <p className="font-medium text-gray-900">
                                {new Date(estagio.dataInicio).toLocaleDateString('pt-BR')} - {new Date(estagio.dataTermino).toLocaleDateString('pt-BR')}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex space-x-2 ml-4">
                        <Button
                          variant="outline"
                          size="sm"
                          asChild
                          title="Ver horários"
                        >
                          <Link to={`/horarios/estagio/${estagio.id}`}>
                            <EyeIcon className="h-4 w-4" />
                          </Link>
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          asChild
                          title="Adicionar novo horário"
                        >
                          <Link to={`/horarios/novo?estagioId=${estagio.id}`}>
                            <PlusIcon className="h-4 w-4" />
                          </Link>
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeletarTodosHorarios(estagio.id, estagio.aluno?.nome || 'Aluno')}
                          disabled={deletingEstagioId === estagio.id}
                          className="hover:bg-red-50 hover:text-red-600 hover:border-red-300"
                          title="Deletar todos os horários"
                        >
                          {deletingEstagioId === estagio.id ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                          ) : (
                            <TrashIcon className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>

                    {/* Preview dos Horários */}
                    {horarios.length > 0 && (
                      <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                        <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                          <ClockIcon className="h-4 w-4 mr-2" />
                          Horários Cadastrados ({horarios.length})
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                          {horarios.slice(0, 6).map((horario) => (
                            <div key={horario.id} className="bg-white p-3 rounded border text-sm">
                              <div className="font-medium text-gray-900">
                                {getDiaSemanaLabel(horario.diaSemana)}
                              </div>
                              <div className="text-gray-600">
                                {formatarHorario(horario.horarioInicio)} - {formatarHorario(horario.horarioFim)}
                              </div>
                            </div>
                          ))}
                          {horarios.length > 6 && (
                            <div className="bg-white p-3 rounded border text-sm flex items-center justify-center text-gray-500">
                              +{horarios.length - 6} mais
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Paginação */}
          {estagiosData && estagiosData.pagination?.totalPages > 1 && (
            <div className="flex justify-center mt-6">
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange((filters.page || 1) - 1)}
                  disabled={(filters.page || 1) <= 1}
                >
                  Anterior
                </Button>
                <span className="flex items-center px-3 py-2 text-sm text-gray-700">
                  Página {filters.page || 1} de {estagiosData.pagination?.totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange((filters.page || 1) + 1)}
                  disabled={(filters.page || 1) >= (estagiosData.pagination?.totalPages || 1)}
                >
                  Próxima
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={dialogState.isOpen}
        onClose={closeDialog}
        onConfirm={dialogState.onConfirm}
        title={dialogState.title}
        message={dialogState.message}
        type={dialogState.type}
        confirmText="Excluir Todos"
        cancelText="Cancelar"
        isLoading={deletingEstagioId !== null}
      />
    </div>
  );
};